/**
 * This component uses a edited version of react-responsive-select (https://benbowes.github.io/react-responsive-select/demo/) to achieve the custom model and pricing behaviour
 * Edits include: allowing a function to be called when an option is selected; allowing a component to be displayed before the select option lisiting
 *
 * Responsive-select model overview
 * all displayed options are passed as an array of objects: [{ text (string), value (string), markup(fn) }]
 * the component highlights selected items by matching the selectedValues array [(string)] against the 'value' of the passed options array item
 */

import React from 'react'
import { array, string, bool, func } from 'prop-types'
import { filter, concat, uniq, without, sortBy, orderBy } from 'lodash'
import ReactResponsiveSelect from './components/react-responsive-select/src/ReactResponsiveSelect'
import {
  ResponsiveSelectFlex,
  FormSubmitButton,
  Arrow
} from './components/ResponsiveSelectStyled'
import aggregator from '../../../lib/aggregator'
import { stringify } from 'querystring'

const makeDefaultTitle = [{ text: 'Makes', value: 'default' }]

const modelDefaultTitle = [{ text: 'All Models', value: 'default' }]

const priceDefaultTitle = [{ text: 'Max Payment', value: 'default' }]

const normaliseData = (property, usedVehicles, mutator) => {
  const counts = aggregator(property, usedVehicles, mutator)
  return Object.keys(counts).map((item) => ({
    text: item,
    value: item.toLowerCase(),
    count: counts[item]
  }))
}

const defaultSelectMarkup = (children) => (
  <div className="rrs__option_padding">{children}</div>
)

class VehicleHeroFilterBar extends React.PureComponent {
  constructor(props) {
    super(props)
    if (!props.usedVehicles || !props.usedVehicles.reduce) return null
    this.state = {
      makeSelectedValues: ['default'],
      modelSelectedValues: ['default'],
      isFocused: false,
      isMakeSelected: props.type === 'vans',
      type: props.type,
      ...this.aggregateMakeData(props.usedVehicles, props.type),
      isPricingStructureMonthly: props.hasMontlyPricing
    }
    this.handleTypeChange = this.handleTypeChange.bind(this)
    this.handleMakeChange = this.handleMakeChange.bind(this)
    this.handleModelChange = this.handleModelChange.bind(this)
    this.handlePriceChange = this.handlePriceChange.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handlePriceFormatChange = this.handlePriceFormatChange.bind(this)
    this.getModelSelectOptions = this.getModelSelectOptions.bind(this)
    this.getMakeSelectOptions = this.getMakeSelectOptions.bind(this)
    this.sortMakeSelectOptions = this.sortMakeSelectOptions.bind(this)
    this.getTypeSelectOptions = this.getTypeSelectOptions.bind(this)
    this.handleSetAllModelBrandType = this.handleSetAllModelBrandType.bind(this)
    this.handleSetAllState = this.handleSetAllState.bind(this)
    this.isClearSelected = this.isClearSelected.bind(this)
    this.isSelectAllSelected = this.isSelectAllSelected.bind(this)
    this.getHeaderModelMarkup = this.getHeaderModelMarkup.bind(this)
    this.isWindowAvailable = typeof window !== 'undefined'
    this.prevModelSelectedValues = []
    this.preventDoubleCallFromBubbling = false
  }

  componentDidMount() {
    this.setType(this.props.type)
  }

  aggregateMakeData(usedVehicles, type) {
    this.filtered = sortBy(
      type
        ? usedVehicles.filter(
            (vehicle) => vehicle.category.toLowerCase() === type
          )
        : usedVehicles,
      'model'
    )
    this.filteredModels = []
    return {
      types: normaliseData('category', usedVehicles),
      makes: normaliseData('brand', this.filtered),
      models: [],
      prices: []
    }
  }

  setType(type) {
    const data = this.aggregateMakeData(this.props.usedVehicles, type)
    let selectedMakes = ['default']
    if (data.makes.length === 1) {
      selectedMakes = [data.makes[0].value]
    }
    this.setState({
      type,
      makeSelectedValues: selectedMakes,
      modelSelectedValues: ['default'],
      isMakeSelected: selectedMakes[0] !== 'default',
      ...data,
      ...this.aggregateModelData(selectedMakes)
    })
  }

  handleTypeChange(newValue) {
    const type = newValue.value
    this.setType(type)
  }

  getTypeSelectOptions() {
    return this.state.types.map((type) => {
      let markup =
        type.value === 'default'
          ? this.titleMarkup()
          : this.multiSelectOptionMarkup(type.text, type.count)
      return {
        text: type.text,
        value: type.value,
        markup: markup,
        count: type.count
      }
    })
  }

  aggregateModelData(makes) {
    this.filteredModels = []
    const normalised = makes.reduce((collection, make) => {
      const filteredModels = [
        ...this.filtered.filter(
          (vehicle) => make === vehicle.brand.toLowerCase()
        )
      ]
      this.filteredModels = [...this.filteredModels, ...filteredModels]
      return [
        ...collection,
        { text: make, value: make + '-header' },
        ...normaliseData('model', filteredModels, (x) => x.toLowerCase())
      ].map((model) => ({ ...model, make }))
    }, [])

    return {
      models: normalised,
      prices: []
    }
  }

  handleMakeChange(newValue) {
    const selected = newValue.options.map((g) => g.value)
    this.setState({
      makeSelectedValues: selected,
      modelSelectedValues: ['default'],
      isMakeSelected:
        newValue.options.length > 0 && newValue.options[0].value !== 'default',
      ...this.aggregateModelData(selected)
    })
  }

  getMakeSelectOptions() {
    const makes = concat(makeDefaultTitle, this.state.makes).map((make) => {
      let markup =
        make.value === 'default'
          ? this.titleMarkup()
          : this.multiSelectOptionMarkup(make.text, make.count)
      return {
        text: make.text,
        value: make.value,
        markup: markup,
        count: make.count
      }
    })
    return this.sortMakeSelectOptions(makes)
  }

  sortMakeSelectOptions(makes) {
    const [defaultOption, ...makesToSort] = makes
    const sortedMakes = []
    const unsortedMakes = []

    makesToSort.map((makeToSort) => {
      const make = this.props.makes.find(
        ({ brandName }) => brandName === makeToSort.value.toLowerCase()
      )
      if (make) {
        sortedMakes[make.order] = makeToSort
      } else {
        unsortedMakes.push(makeToSort)
      }
    })

    return [
      defaultOption,
      ...sortedMakes,
      ...orderBy(unsortedMakes, ['count', 'text'], ['desc', 'asc'])
    ]
  }

  // MARK : Models Dropdown

  isHeader(value) {
    return value.slice(value.indexOf('-') + 1) === 'header'
  }

  isClearSelected(brand) {
    const { modelSelectedValues } = this.state
    const selectedBrandOnlyItemsValues = this.getBrandItems(
      modelSelectedValues,
      brand
    )
    return selectedBrandOnlyItemsValues.length
  }

  isSelectAllSelected(brand) {
    const { modelSelectedValues } = this.state
    const incomingBrandOnlyItemsValues = this.getBrandItems(
      this.state.models,
      brand
    )
    const selectedBrandOnlyItemsValues = this.getBrandItems(
      modelSelectedValues,
      brand
    )
    return (
      incomingBrandOnlyItemsValues.length ===
      selectedBrandOnlyItemsValues.length
    )
  }

  getBrandItems(options, brand) {
    if (options.length === 0) return []
    const results = filter(options, (n) => {
      const value = n.value || n
      return value.slice(0, value.indexOf('-')) === brand
    })
    return typeof options[0] === 'string'
      ? results
      : results.map((e) => e.value)
  }

  getBrandCount(options, brand) {
    return (
      filter(options, (n) => {
        return n.slice(0, n.indexOf('-')) === brand
      }).length - 1
    )
  }

  aggregatePriceData(models, isMonthly = true) {
    const bucketSize = isMonthly ? 25 : 500
    const priceType = isMonthly ? 'monthlyPayment' : 'value'
    const filtered = models.reduce((collection, model) => {
      return [
        ...collection,
        ...this.filteredModels.filter((v) => v.model.toLowerCase() === model)
      ]
    }, [])
    const sorted = sortBy(filtered, priceType)
    const aggregated = sorted.reduce((collection, vehicle) => {
      const rounded = Math.ceil(vehicle[priceType] / bucketSize) * bucketSize
      if (!collection[rounded]) collection[rounded] = 0
      collection[rounded] += 1
      return collection
    }, {})
    let runningCount = 0
    const prices = Object.keys(aggregated).reduce((collection, key) => {
      runningCount += aggregated[key]
      collection[key] = runningCount
      return collection
    }, {})
    return {
      prices: Object.keys(prices).map((price) => ({
        text: `Up to £${price}`,
        value: price,
        count: prices[price]
      }))
    }
  }

  handleModelChange(newValue) {
    const selected = newValue.options.map((g) => g.value)
    this.setState({
      modelSelectedValues: selected,
      ...this.aggregatePriceData(selected, this.state.isPricingStructureMonthly)
    })
  }

  handleSetAllModelBrandType(model, clear = false) {
    // catch a double event call after 'clear' is pressed
    if (!this.preventDoubleCallFromBubbling) {
      let outgoingSelectedValues
      const { modelSelectedValues } = this.state
      const incomingBrandOnlyItemsValues = this.getBrandItems(
        this.state.models,
        model.brand
      )
      const selectedBrandOnlyItemsValues = this.getBrandItems(
        modelSelectedValues,
        model.brand
      )
      // compare length (minus header) with the original list so we can determine if we should clear or select
      if (
        incomingBrandOnlyItemsValues.length - 1 !==
          selectedBrandOnlyItemsValues.length &&
        !clear
      ) {
        // get a unique combined list of currently selected values and incoming matched brand values
        outgoingSelectedValues = uniq(
          concat(modelSelectedValues, incomingBrandOnlyItemsValues)
        )
      } else {
        // else remove all brand items from the current selected values
        outgoingSelectedValues = without(
          modelSelectedValues,
          ...incomingBrandOnlyItemsValues
        )
      }
      // if no selection exists, reset selected values to the empty 'default'
      if (outgoingSelectedValues.length === 0) {
        this.setState({ modelSelectedValues: ['default'] })
      } else {
        this.setState({ modelSelectedValues: outgoingSelectedValues })
      }
      if (clear) {
        this.preventDoubleCallFromBubbling = true
      }
    } else {
      this.preventDoubleCallFromBubbling = false
    }
    // this.setState({modelSelectOptions: this.getModelSelectOptions()})
  }

  handleSetAllState(model) {
    const { modelSelectedValues } = this.state
    const incomingBrandOnlyItemsValues = this.getBrandItems(
      this.state.models,
      model.brand
    )
    const selectedBrandOnlyItemsValues = this.getBrandItems(
      modelSelectedValues,
      model.brand
    )
    if (
      incomingBrandOnlyItemsValues.length !==
      selectedBrandOnlyItemsValues.length
    ) {
      let outgoingSelectedValues = without(
        modelSelectedValues,
        `${model.brand}-header`
      )
      this.setState({ modelSelectedValues: outgoingSelectedValues })
    }
    // this.setState({modelSelectOptions: this.getModelSelectOptions()})
  }

  getModelSelectOptions(modelSelectedValues = this.state.modelSelectedValues) {
    return [...modelDefaultTitle, ...this.state.models].map((model) => {
      let count = this.getBrandCount(modelSelectedValues, model.brand)
      let markup =
        model.value === 'default'
          ? this.titleMarkup(count)
          : this.multiSelectOptionMarkup(model.text, model.count)
      markup = this.isHeader(model.value)
        ? this.getHeaderModelMarkup(model.text, model.make, count)
        : markup
      // assign onClick and pass through the model info
      const clickfn = this.isHeader(model.value)
        ? (event) => {
            event.stopPropagation()
            this.handleSetAllModelBrandType(model)
          }
        : () => this.handleSetAllState(model)
      return {
        text: model.text,
        value: model.value,
        markup: markup,
        count: model.count,
        onClick: clickfn
      }
    })
  }

  // MARK : Price Dropdown

  handlePriceChange(newValue) {
    const formValue = {
      [newValue.name]: newValue.value
    }
    this.setState({ ...formValue })
  }

  handlePriceFormatChange(isMonthly) {
    const priceOption = this.getPriceSelectOptions(
      isMonthly ? this.state.prices.pm : this.state.prices.fp
    )
    this.setState({
      priceSelectOptions: priceOption,
      isPricingStructureMonthly: isMonthly,
      ...this.aggregatePriceData(this.state.modelSelectedValues, isMonthly)
    })
  }

  handleSubmit(e) {
    e.preventDefault()
    if (!this.props.onSearch) return null
    const params = {}
    const makes = this.state.makeSelectedValues.join(',').toLowerCase()
    const models = this.state.modelSelectedValues
      .filter((model) => !model.includes('-header'))
      .join(',')
      .toLowerCase()
    if (makes && makes !== 'default') params.brand = makes
    if (models && models !== 'default') params.model = models
    if (this.state.price && this.state.price !== 'default') {
      if (this.state.isPricingStructureMonthly) {
        params.monthlyPaymentMin = 0
        params.monthlyPaymentMax = this.state.price
      } else {
        params.valueMin = 0
        params.valueMax = this.state.price
      }
    }

    let queryString = Object.keys(params).length ? `?${stringify(params)}` : ''
    this.props.onSearch(`/${this.state.type}/used/${queryString}`)
  }

  getPriceSelectOptions() {
    const prices = [...priceDefaultTitle, ...this.state.prices].map((price) => {
      let markup =
        price.value === 'default'
          ? this.titleMarkup()
          : this.selectOptionMarkup(price.text, price.count)
      return {
        text: price.text,
        value: price.value,
        markup: markup,
        count: price.count
      }
    })

    return [
      ...prices,
      {
        text: 'No Maximum',
        value: null,
        markup: defaultSelectMarkup(
          this.state.modelSelectedValues[0] === 'default'
            ? 'Select a Model'
            : 'No Maximum'
        )
      }
    ]
  }

  // MARK: Markup

  getPriceSwitcherMarkup() {
    return (
      <div className="rrs__option_heading rrs__option_center rrs__option_heading_action rrs__option_padding">
        <span
          className={`${
            this.state.isPricingStructureMonthly ? 'active' : ''
          } rrs__option_heading_action rrs__option_heading__tab`}
          onClick={() => this.handlePriceFormatChange(true)}
          data-label-short="MTH"
        >
          <span>Monthly</span>
        </span>
        <span
          className={`${
            !this.state.isPricingStructureMonthly ? 'active' : ''
          } rrs__option_heading_action rrs__option_heading__tab`}
          onClick={() => this.handlePriceFormatChange(false)}
        >
          Full
        </span>
      </div>
    )
  }

  getHeaderModelMarkup(text, brand) {
    let clearaction =
      this.isClearSelected(brand) > 0 ? (
        <span
          className="rrs__option_clear rrs__option_heading_action"
          onClick={() =>
            this.handleSetAllModelBrandType({ brand: brand }, true)
          }
        >
          clear ({this.isClearSelected(brand)})
        </span>
      ) : (
        ''
      )
    return (
      <div>
        <div className="rrs__option_heading rrs__option_heading_action rrs__option_padding">
          {text} {clearaction}
        </div>
      </div>
    )
  }

  multiSelectOptionMarkup(text, count) {
    return (
      <div className="rrs__option_padding">
        {this.multiselectCheckBoxMarkUp()}
        <span>
          {text}
          {count && <span> ({count})</span>}
        </span>
      </div>
    )
  }

  selectOptionMarkup(text, count) {
    return (
      <div className="rrs__option_padding">
        {text}
        {count && <span> ({count})</span>}
      </div>
    )
  }

  titleMarkup() {
    return <div />
  }

  multiselectCheckBoxMarkUp() {
    return (
      <span className="checkbox">
        <svg
          className="checkbox-icon"
          width="19px"
          height="15px"
          viewBox="0 0 19 15"
        >
          <g transform="translate(-677.000000, -765.000000)" fillRule="nonzero">
            <polygon points="679.475745 773.702311 682.279767 776.534215 693.524255 765.261846 695 766.749397 682.279767 779.519868 678 775.189863" />
          </g>
        </svg>
      </span>
    )
  }

  render() {
    return (
      <div>
        <form onSubmit={this.handleSubmit}>
          <ResponsiveSelectFlex justify="space-between" mx={['8%', 0]}>
            <ReactResponsiveSelect
              name="type"
              options={this.getTypeSelectOptions()}
              caretIcon={<Arrow open />}
              selectedValue={this.state.type}
              onChange={this.handleTypeChange}
              altered
            />
            <ReactResponsiveSelect
              multiselect
              name="make"
              selectedValues={this.state.makeSelectedValues}
              options={this.getMakeSelectOptions()}
              caretIcon={<Arrow open />}
              onChange={this.handleMakeChange}
              altered={this.state.makeSelectedValues[0] !== 'default'}
            />

            <ReactResponsiveSelect
              multiselect
              name="model"
              disabled={!this.state.isMakeSelected}
              selectedValues={this.state.modelSelectedValues}
              options={this.getModelSelectOptions()}
              caretIcon={<Arrow open />}
              onChange={this.handleModelChange}
              altered
              customLabelRenderer={(multiSelectSelectedOptions) => {
                if (multiSelectSelectedOptions.options) {
                  if (multiSelectSelectedOptions.options.length > 1)
                    return `${multiSelectSelectedOptions.options.length} Models Selected`
                  // else multiSelectSelectedOptions.options[0]
                } else {
                  return 'default'
                }
                return (
                  multiSelectSelectedOptions.options &&
                  multiSelectSelectedOptions.options.length &&
                  multiSelectSelectedOptions.options
                    .map((v) => v.text)
                    .join(', ')
                )
              }}
            />

            <ReactResponsiveSelect
              name="price"
              headerComponent={
                this.props.hasMontlyPricing && this.getPriceSwitcherMarkup()
              }
              disabled={!this.state.isMakeSelected}
              selectedValue={this.state.price}
              options={this.getPriceSelectOptions()}
              caretIcon={<Arrow open />}
              onChange={this.handlePriceChange}
              altered={this.state.price !== 'default'}
            />

            <FormSubmitButton>Search {this.props.searchlabel}</FormSubmitButton>
          </ResponsiveSelectFlex>
        </form>
      </div>
    )
  }
}
VehicleHeroFilterBar.defaultProps = {
  type: null,
  usedVehicles: array,
  searchlabel: 'Used',
  hasMontlyPricing: true
}
VehicleHeroFilterBar.propTypes = {
  type: string,
  usedVehicles: array,
  searchlabel: string,
  hasMontlyPricing: bool,
  onSearch: func
}
export default VehicleHeroFilterBar
